import React from "react";
import { motion } from "framer-motion";
import s from "../ComparisonTable.module.scss";
import cn from "classnames";
import IconWithColor from "../../Common/IconWithColor";
import { useTranslation } from "next-i18next";

type SectionProps = {
  title?: string;
  isExpanded?: boolean;
  onToggle?: () => void;
  hasMultipleSections?: boolean;
  columnCount?: number;
} & React.HTMLAttributes<HTMLDivElement>;

const Section: React.FC<SectionProps> = ({
  title,
  isExpanded = false,
  onToggle,
  hasMultipleSections = false,
  columnCount = 0,
  ...props
}) => {
  const { t } = useTranslation();
  const handleClick = () => {
    if (hasMultipleSections && onToggle) {
      onToggle();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (
      hasMultipleSections &&
      onToggle &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      className={cn(s.section, {
        [s.expanded]: isExpanded,
      })}
      {...props}
    >
      <div
        className={cn(s.sectionRow, s.row, {
          [s.clickable]: hasMultipleSections,
        })}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={hasMultipleSections ? "button" : undefined}
        tabIndex={hasMultipleSections ? 0 : undefined}
        aria-expanded={hasMultipleSections ? isExpanded : undefined}
        aria-label={hasMultipleSections && title ? t("toggle-section", { sectionName: title }) : undefined}
      >
        {/* Sticky left portion - similar to feature cell */}
        <div
          className={cn(s.sectionCell, s.sectionFeatureCell, s.stickyShadow)}
        >
          <motion.div transition={{ duration: 0.3 }} className={s.iconWrapper}>
            <IconWithColor
              icon={isExpanded ? "minus" : "add"}
              width={20}
              height={20}
              alt={title || ""}
              color={"black"}
              className={s.toggleIcon}
            />
          </motion.div>

          {title && <span className={s.title}>{title}</span>}
        </div>

        {/* Generate placeholder cells to match data column count */}
        {columnCount > 0 ? (
          Array.from({ length: columnCount }).map((_, index) => (
            <div key={index} className={cn(s.sectionCell, s.sectionDataCell)} />
          ))
        ) : (
          <div className={cn(s.sectionCell, s.sectionSpan)} />
        )}
      </div>
    </div>
  );
};

export default Section;
